import { useCallback, useEffect, useMemo, useState } from 'react';

import {
  countValidationStatuses,
  normalizeValidationStatus,
  splitValidationGroup,
  validationTabRank,
  VALIDATION_DEFAULT_PAGE_SIZE,
  VALIDATION_PAGE_SIZES,
} from './validationPanelShared';

function mapItem(row, detailById) {
  const id = row.id;
  return {
    id,
    label: row.label || row.requirement_code,
    satisfied: Boolean(row.satisfied),
    resultStatus: row.result_status || (row.satisfied ? 'Yes' : 'No'),
    groupName: row.group_name || 'Validation',
    detail: detailById[id] ?? row.detail ?? undefined,
  };
}

/**
 * Server-paged validation evidence for Digitize (no full 5k-rule download).
 */
export function useValidationEvidencePage({
  assetId,
  fetchEvidencePage,
  fetchEvidenceDetails,
  reloadToken = 0,
}) {
  const active = Boolean(assetId && fetchEvidencePage);
  const [pageData, setPageData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [activeTab, setActiveTab] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [query, setQuery] = useState('');
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(VALIDATION_DEFAULT_PAGE_SIZE);
  const [detailById, setDetailById] = useState({});

  const requestPage = useCallback(async () => {
    if (!active) return;
    setLoading(true);
    setLoadError('');
    try {
      const data = await fetchEvidencePage(assetId, {
        page,
        pageSize,
        tab: activeTab || undefined,
        status: statusFilter || undefined,
        q: query.trim() || undefined,
      });
      setPageData(data);
      if (data?.tab && data.tab !== activeTab) {
        setActiveTab(data.tab);
      }
    } catch (error) {
      setPageData(null);
      setLoadError(error?.message || 'Unable to load validation rules.');
    } finally {
      setLoading(false);
    }
  }, [
    active,
    assetId,
    fetchEvidencePage,
    page,
    pageSize,
    activeTab,
    statusFilter,
    query,
  ]);

  useEffect(() => {
    if (!active) return undefined;
    const needle = query.trim();
    const delay = needle.length >= 2 ? 350 : 0;
    const timer = setTimeout(() => {
      requestPage();
    }, delay);
    return () => clearTimeout(timer);
  }, [active, requestPage, reloadToken]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, statusFilter, query, pageSize, reloadToken]);

  const ensureDetails = useCallback(
    async (ids) => {
      if (!fetchEvidenceDetails || !ids.length) return;
      const missing = ids.filter((id) => detailById[id] === undefined);
      if (!missing.length) return;
      const response = await fetchEvidenceDetails(assetId, missing);
      const details = response?.details || {};
      setDetailById((previous) => {
        const next = { ...previous };
        Object.entries(details).forEach(([key, value]) => {
          next[key] = value;
        });
        return next;
      });
    },
    [assetId, fetchEvidenceDetails]
  );

  const normalized = useMemo(() => {
    if (!pageData?.items) return [];
    return pageData.items.map((row) => {
      const mapped = mapItem(row, detailById);
      const { tab, group } = splitValidationGroup(mapped.groupName);
      return {
        ...mapped,
        tab,
        group,
        status: normalizeValidationStatus(mapped.resultStatus || (mapped.satisfied ? 'Yes' : 'No')),
      };
    });
  }, [pageData, detailById]);

  const tabs = useMemo(() => {
    const raw = pageData?.tabs || [];
    return raw
      .map((tab) => ({
        name: tab.name,
        total: tab.total,
        counts: tab.counts || {},
      }))
      .sort((a, b) => validationTabRank(a.name) - validationTabRank(b.name) || a.name.localeCompare(b.name));
  }, [pageData]);

  const counts = useMemo(() => {
    const c = pageData?.status_counts;
    if (c) {
      return { Yes: c.Yes || 0, No: c.No || 0, NA: c.NA || 0, Error: c.Error || 0 };
    }
    return countValidationStatuses(normalized);
  }, [pageData, normalized]);

  const groups = useMemo(() => {
    const map = new Map();
    for (const item of normalized) {
      if (!map.has(item.group)) map.set(item.group, []);
      map.get(item.group).push(item);
    }
    return Array.from(map.entries()).map(([name, groupItems]) => ({ name, items: groupItems }));
  }, [normalized]);

  const filesTotal = pageData?.files_total ?? groups.length;
  const currentPage = pageData?.page ?? page;
  const pageCount = pageData?.page_count ?? 1;
  const totalRuleCount = pageData?.total_count ?? 0;

  return {
    active,
    loading,
    loadError,
    normalized,
    tabs,
    counts,
    groups,
    pagedGroups: groups,
    pagedItems: normalized,
    filesTotal,
    currentPage,
    pageCount,
    page,
    setPage,
    pageSize,
    setPageSize,
    activeTab,
    setActiveTab,
    statusFilter,
    setStatusFilter,
    query,
    setQuery,
    ensureDetails,
    totalRuleCount,
    pageSizes: VALIDATION_PAGE_SIZES,
  };
}
